import graphql__7778e03bdf58833d3f70 from "./__generated__/ContributionsDatavizGraphAnimation_participativeModule.graphql.js";import { useCallback, useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import { debounce } from "lodash";

import d3 from "../../../../lib/d3";
import ContributionsDatavizThemeTooltip from "./ContributionsDatavizThemeTooltip";
import { useFormatMessage } from "../../../Intl/Context";
import { useWindowSize } from "../../../Utils/useWindowResizedEffect";
import { useNavigateTo } from "../../../Routes/RoutesContext";
import Icon from "../../../Utils/Icon";
import { useResponsiveFlag } from "../../../Utils/ResponsiveQueries";
import classNames from "classnames";
import { BoxSpinner } from "../../../Utils/Spinner";

const debouncedAnimate = debounce(animate, 300);

// Make the simulations only execute in 50 iterations, instead of the default 300, to make
// them faster and avoid too much user waiting between the pre-render and animation
// phases, before the final visualization is displayed.
//
// See : https://github.com/d3/d3-force#simulation_alphaDecay
const SIMULATION_PRE_RENDER_ITERATIONS_COUNT = 100;
const SIMULATION_ALPHA_DECAY =
  1 - Math.pow(0.001, 1 / SIMULATION_PRE_RENDER_ITERATIONS_COUNT);

// Maximum number of tries to rescale the simulation to the best size before we keep the
// current size, even if we could optimize it a bit more. This is needed for certain sizes
// and configurations of animations where the algorithm throuws stack overflow errors if
// not limited this way.
const MAX_RESIZES_COUNT = 10;

// Define the padding used between theme contribution nodes circles in the contributions
// cluster graph when hovering a theme circle.
const THEME_CONTRIBUTIONS_NODE_PADDING = 5;

// Define the minim graph margin inside its container
const GRAPH_MARGIN_MIN = 20;

const ContributionsDatavizGraphAnimation = props => {
  const participativeModule = useFragment(
    ContributionsDatavizGraphAnimation_participativeModule,
    props.participativeModule
  );
  const participativeModuleSlug = participativeModule.slug;
  const { themes } = participativeModule;

  const navigateTo = useNavigateTo();

  const navigateToTheme = useCallback(
    ({ slug: themeSlug }) =>
      navigateTo("datavizGraphTheme", { participativeModuleSlug, themeSlug }),
    [navigateTo, participativeModuleSlug]
  );

  // The hook returned function is built here to be provided to the tooltips, as they're
  // rendered through the ReactDOMServer.renderToString function, which does not provide
  // any context, so the `intl` context cannot be fetched from the sub-component.
  const formatMessage = useFormatMessage();

  const [container, setContainer] = useState();
  const { windowWidth } = useWindowSize();
  const { isDefaultScreenWidth } = useResponsiveFlag();
  const isLargeDevice = isDefaultScreenWidth;

  // The graph is resized when the window is resized to allow maxmimizing the graph size
  // inside the parent container.
  useEffect(() => {
    if (!container) return;

    debouncedAnimate(container, navigateToTheme, formatMessage, themes, isLargeDevice);

    return () => d3.hideAllTooltips();
  }, [container, navigateToTheme, formatMessage, themes, windowWidth, isLargeDevice]);

  return (
    <div id="contributions-dataviz-graph-animation" ref={setContainer}>
      <BoxSpinner className="contributions-dataviz-graph-spinner" />
    </div>
  );
};

// The animate function is called 3 times per render, and works in 2 phases :
//
//   1. The "pre-render" phase : The first phase is meant to pre-render the graph by
//      statically (without visual animation, and instantly) generating the final
//      positions of the theme circles, which allows us to define the size of the graph.
//
//      This phase calls the `animate()` function 2 times : the first time to calcualte
//      the scale ratio of the circles, and the second to center the final graph inside
//      the container to make it look cleaner.
//
//      The calculation of the ratio to resize the theme circles allows to :
//        - reduce its size to fit the container size, if the first generated graph size
//          is greater than its container, by passing a ratio < 1.
//        - increase its size to fill as much of the container as possible, if the
//          first generated graph size is smaller than its container, by passing a
//          ratio > 1.
//
//   2. The "animation" phase : The second phase is the actually displayed phase, where
//      theme circles are rendered small and in the center of the graph, and then animated
//      to take their final places visually.
//
function animate(
  container,
  navigateToTheme,
  formatMessage,
  themes,
  isLargeDevice,
  scaleRatio = null,
  containerOffsets = null,
  resizesCount = 0
) {
  // Hide all displayed d3 tooltips to ensure that if a tooltip was still displayed after
  // transitionning from another d3 view, we hide it and avoid it to ghost around.
  d3.hideAllTooltips();

  // Define whether we're going to display the animation of the circles or not. The first
  // animate() call from the component does not set a scaleRatio and should run the
  // simulation in a static way, to allow to quickly calculate the scaleRatio that we'll
  // need to apply to the circles radius to optimize the space occupied by the animation,
  // and avoid container overflow masking in some edge cases.
  const isPreRenderPhase = scaleRatio === null || containerOffsets === null;
  const isAnimationPhase = !isPreRenderPhase;

  const buildThemeNode = (theme, parentTheme = theme) => {
    const {
      id,
      slug,
      name,
      iconClass,
      contributionsCount,
      reactionsCount,
      children,
      primaryColor,
      primaryContrastColor
    } = theme;

    const contributions = theme.contributions.edges;

    return {
      id,
      slug,
      name,
      contributionsCount,
      reactionsCount,
      color: primaryColor,
      textColor: primaryContrastColor,
      icon: iconClass,
      parentTheme: {
        id: parentTheme.id,
        name: parentTheme.name
      },
      questions: children,
      contributions
    };
  };

  // Create the theme nodes list that contain all the needed theme data and is used as the
  // base of the d3 force simuation to also contain the positional data that's then used
  // to position and/or animate the theme circles.
  const themeNodes = themes.reduce((nodes, theme) => {
    if (theme.children.length) {
      theme.children.forEach(subTheme => {
        const node = buildThemeNode(subTheme, theme);
        nodes.push(node);
      });
    } else {
      const node = buildThemeNode(theme);
      nodes.push(node);
    }

    return nodes;
  }, []);

  const containerWidth = container.getBoundingClientRect().width;
  const containerHeight = containerWidth;

  d3.hideAllTooltips();

  if (isAnimationPhase) {
    d3.select(container).select(".contributions-dataviz-graph-spinner").remove();
  }

  // Clear existing SVG before redrawing
  d3.select(container).select("svg").remove();

  const containerTranslateX = containerWidth / 2 - (containerOffsets?.x || 0);
  const containerTranslateY = containerHeight / 2 - (containerOffsets?.y || 0);

  const graphContainer = d3
    .select(container)
    .append("svg")
    .attr("width", containerWidth)
    .attr("height", containerHeight)
    .attr("class", "d-block mx-auto mt-2")
    .attr("aria-hidden", "true")
    .append("g")
    .attr(
      "transform",
      "translate(" + containerTranslateX + "," + containerTranslateY + ")"
    );

  const themeWeight = themeNode =>
    themeNode.contributionsCount + themeNode.reactionsCount / 5;

  // Defines the minimum and max number of contributions of the themes, and is used to
  // adjust the `radius()` function output values.
  //
  // The values are adjusted to avoid big differences between the minimum and maximum if
  // theme weights are close to each other, to avoid having a circle that is too big
  // compared to others that have a similar weight.
  const minContributionsWeight = Math.round(d3.min(themeNodes, themeWeight) / 5);
  const maxContributionsWeight = Math.round(d3.max(themeNodes, themeWeight));
  const [min, max] = [minContributionsWeight, maxContributionsWeight];
  // Allows scaling the size of the theme circles and ensures that during the "pre-render"
  // phase we have a scale ratio of 1.
  const currentScaleRatio = scaleRatio || 1;
  // Determines the reduction ratio to apply to the final radius depending on the total
  // window size, allowing us to reduce the size of the graph for small devices
  const radiusReductionRatio = 0.08;
  // Define minimum and maximum theme circles radius to adjust the output values of the
  // `radius()` function.
  const minRadius = 20 * currentScaleRatio;
  const maxRadius =
    containerWidth *
    (radiusReductionRatio + 1 / (themeNodes.length + 3)) *
    currentScaleRatio;
  // This function defines the final radius of the theme circles depending on the number
  // of contributions that are attached to them.
  const radius = d3.scaleSqrt().domain([min, max]).range([minRadius, maxRadius]);

  // Build the `themeClusters` object to make the below `forceCluster` force work.
  // Ensure that the theme with the greatest number of contributions (and thereforce,
  // the biggest size) is considered as the attraction pole of the cluster.
  const themeClusters = themeNodes.reduce((clusters, themeNode) => {
    const { id: parentThemeId } = themeNode.parentTheme;

    const shouldDefineThemeAsClusterCenter =
      !clusters[parentThemeId] ||
      themeNode.contributionsCount > clusters[parentThemeId].contributionsCount;

    if (!shouldDefineThemeAsClusterCenter) return clusters;

    return { ...clusters, [parentThemeId]: themeNode };
  }, {});

  // This is a custom force that's used in the force simulation and tries to group
  // (clusterize) the theme circles by parent theme.
  const forceCluster = alpha => {
    const k = alpha * 0.09;
    const n = themeNodes.length;

    for (let i = 0; i < n; ++i) {
      let themeNode = themeNodes[i];
      let cluster = themeClusters[themeNode.parentTheme.id];

      themeNode.vx -= (themeNode.x - cluster.x) * k;
      themeNode.vy -= (themeNode.y - cluster.y) * k;
    }
  };

  // This is the system that places the different theme circles in the space. It works
  // with a "force simulation" system that's applied to the theme nodes.
  //
  // The different forces that move the theme circles are the following :
  //   - collide : it makes the nodes repel each other so that they don't overlap,
  //               simulating a "collision" force, around the circle.
  //   - cluster : a custom force that tries to group the different sub-themes of a given
  //               theme around the theme that has the greater amount of contributions.
  //   - charge  : it uses the "many-body" force with a negative strength, which makes
  //               each node repel each other, like magnets of the same pole would.
  //   - x       : Pushes the nodes to the X center of the graph
  //   - y       : Pushes the nodes to the Y center of the graph
  //
  // Note that the forces simulation happens on the `themeNodes` elements, which are data
  // objects. They're not automatically reported to the displayed graph objects. To make
  // the graph objects be visually updated when the simulation progresses, we neeed to
  // update the graph objects.
  //
  // Depending on the current render phase :
  //   1. During the "pre-render" phase, we first run the whole simumation manually, then
  //      render the graph objects, so their positions are already set to their final
  //      values when they're rendered.
  //   2. During the "animation" phase, we listen to the simulation's "tick" event and
  //      progressively update the different objects coordinates while the simulation
  //      makes the position values evolve.
  const simulation = d3
    .forceSimulation(themeNodes)
    .force(
      "collide",
      d3
        .forceCollide()
        // The collision edges are calculated with the radius of the node and adding 2 to
        // have some margins between theme circles.
        .radius(d => radius(d.contributionsCount) + 2)
        // Using `iterations(10)` makes the collision force strong to avoid them to
        // overlap at anytome. This parameter defaults to 1 and allows some overlapping
        // during the animations, which messes a bit the animation.
        .iterations(10)
    )
    .force("cluster", forceCluster)
    .force("charge", d3.forceManyBody().strength(-30))
    .force("x", d3.forceX().strength(0.3))
    .force("y", d3.forceY().strength(0.3))
    // Define the number of iterations of the simulation
    .alphaDecay(SIMULATION_ALPHA_DECAY);

  // Stop the simulation so we can define ourselves when we run it depending on the
  // current phase of the rendering.
  simulation.stop();

  // If we're in the "pre-render" phase and we shouldn't being animating the circles,
  // we manually run the simulation, so nodes are immediately applied their final position
  // when they're rendered.
  //
  // This allows us to calculate the final size of the graph and call the animation
  // function back with a `scaleRatio` that makes it correctly fill the available space.

  if (isPreRenderPhase) {
    const simulationTotalTicksCount = Math.ceil(
      Math.log(simulation.alphaMin()) / Math.log(1 - SIMULATION_ALPHA_DECAY)
    );

    simulation.tick(simulationTotalTicksCount);
  }

  // Create the theme group objects in the visualization that will contain all theme
  // related elements : the circle, the label and the contributions inner cluster
  // displayed on hover.
  const themeNodeGroups = graphContainer
    .selectAll("div")
    .data(themeNodes)
    .enter()
    .append("g")
    .attr("class", "group");

  // Create the colored theme circles inside the theme nodes groups
  const themeCircles = themeNodeGroups
    .append("circle")
    .attr("class", "theme-circle")
    .attr("r", d => (isAnimationPhase ? minRadius / 2 : radius(d.contributionsCount)))
    .attr("fill", d => d.color)
    .attr("cx", d => d.x)
    .attr("cy", d => d.y);

  // If we're in the graph "pre-render" phase, now that we have run the simulation and
  // rendered the theme circles
  if (isPreRenderPhase) {
    const graph = graphContainer._groups[0][0];
    const graphBoundingRect = graph.getBoundingClientRect();
    const { width: graphWidth, height: graphHeight } = graphBoundingRect;

    let newScaleRatio = scaleRatio;

    const widthRatio = (containerWidth - GRAPH_MARGIN_MIN) / graphWidth;
    const heightRatio = (containerHeight - GRAPH_MARGIN_MIN) / graphHeight;
    const targetRatio = Math.min(widthRatio, heightRatio);

    const willIgnoreResize = resizesCount > MAX_RESIZES_COUNT && targetRatio > 1;
    const shouldResize = !newScaleRatio || targetRatio < 1 || targetRatio > 1.05;

    if (shouldResize && !willIgnoreResize) {
      const previousScaleRatio = scaleRatio || 1;
      newScaleRatio = previousScaleRatio + targetRatio - 1;
    }

    let newContainerOffsets = null;

    // If we're in the 2nd "pre-render" phase and we already had a scaleRatio applied,
    // calculate the offset of the container to center it in the view.
    if (newScaleRatio === scaleRatio) {
      const { x: graphX, y: graphY } = graphBoundingRect;
      const { x: containerX, y: containerY } =
        graph.parentElement.getBoundingClientRect();

      // Calculate the offsets that we need to center the graph inside the svg container
      const targetXOffset = (containerWidth - graphWidth) / 2;
      const targetYOffset = (containerHeight - graphHeight) / 2;
      // Calculate the current offsets that we have after rendering to the final dimension
      const currentXOffset = graphX - containerX;
      const currentYOffset = graphY - containerY;
      // Calculate the offsets we need to apply on the next run to move the graph to the
      // target offsets.
      const xOffset = currentXOffset - targetXOffset;
      const yOffset = currentYOffset - targetYOffset;

      newContainerOffsets = { x: xOffset, y: yOffset };
    }

    return animate(
      container,
      navigateToTheme,
      formatMessage,
      themes,
      isLargeDevice,
      newScaleRatio,
      newContainerOffsets,
      ++resizesCount
    );
  }

  // Add/remove the "hover" class to the theme node groups when hovering them.
  themeNodeGroups
    .on("mouseover", (d, i, j) => {
      d3.select(j[i]).classed("hover", true);
    })
    .on("mouseout", (d, i, j) => {
      d3.select(j[i]).classed("hover", false);
    });

  // Prepare the d3 tooltip that displays theme informations and will be displayed when
  // hovering the theme.
  const themeTooltip = d3
    .createTooltip()
    .attr("class", "d3-tip")
    .offset([-15, 0])
    .html(subTheme => {
      return ReactDOMServer.renderToString(
        <ContributionsDatavizThemeTooltip
          formatMessage={formatMessage}
          theme={subTheme}
        />
      );
    });

  themeCircles
    .call(themeTooltip)
    .on("mouseover", themeTooltip.show)
    .on("mouseout", themeTooltip.hide)
    .on("click", themeNode => {
      d3.hideAllTooltips();
      setTimeout(() => navigateToTheme(themeNode), 0);
    });

  // Defines the side length of the inner square of a given theme node circle, to define
  // the space that we have to display the textual informations inside the theme circle.
  const getSideLength = themeNode => {
    return (radius(themeNode.contributionsCount) * 2) / Math.sqrt(2);
  };

  // Add the icon and text label on the themes
  themeNodeGroups
    .append("foreignObject")
    .attr("class", "label")
    .attr("width", d => getSideLength(d))
    .attr("height", d => getSideLength(d))
    .style("overflow", "visible")
    .style("opacity", 0)
    .append("xhtml:div")
    .attr("class", "label-innner")
    .style("color", d => d.textColor)
    .html((d, _i) => {
      const sideLength = getSideLength(d);
      const linesCount = Math.floor((sideLength - 35) / 20);
      const displayLabel = linesCount > 0 && sideLength > 40 && isLargeDevice;
      const displayIdeasCount = sideLength > 60 && isLargeDevice;
      const contributionsLabel = formatMessage(intlMessages.contributions, {
        count: d.contributionsCount
      });

      const iconClassName = classNames("icon", d.icon, {
        "icon-with-label": displayLabel
      });

      return ReactDOMServer.renderToString(
        <>
          <div className={iconClassName} />

          {displayLabel && (
            <div className="description text-center text-small w-100">
              <div style={{ WebkitLineClamp: linesCount }}>{d.name}</div>
            </div>
          )}

          {displayIdeasCount && (
            <div className="reaction text-truncate w-100">
              <Icon className="icon-pencil align-middle" />
              &nbsp;
              {contributionsLabel}
            </div>
          )}
        </>
      );
    });

  // Animate the size of the theme circles so they reach 100% of their final radius size
  // after 1 second.
  themeCircles
    .transition()
    .duration(1000)
    .attr("r", themeNode => radius(themeNode.contributionsCount));

  // Animate the opacity of the themes labels
  themeNodeGroups
    .select(".label")
    .transition()
    .duration(300)
    .delay(700)
    .style("opacity", 1);

  // Create the theme contribution nodes data that will allow to display the contribution
  // circles when hovering the theme circle.
  const themeContributionNodes = themeNodes.map(themeNode => {
    const contributionsNode = {
      children: themeNode.contributions,
      color: themeNode.color,
      contributionsCount: themeNode.contributionsCount
    };

    const paddingCallback = d3
      .hierarchy(contributionsNode)
      .sum(d => (d.children ? 0 : 1 + d.node.reactionsCount))
      .sort((a, b) => b.value - a.value);

    return d3
      .pack()
      .size([
        radius(themeNode.contributionsCount) * 1.8,
        radius(themeNode.contributionsCount) * 1.8
      ])
      .padding(THEME_CONTRIBUTIONS_NODE_PADDING)(paddingCallback);
  });

  // Prepare the contribution circles container that's displayed when hovering the theme
  // circles.
  const themeInnerContributionsCluster = themeNodeGroups
    .append("g")
    .attr("class", "idee");

  themeInnerContributionsCluster
    .selectAll("g")
    .data((_d, i) => themeContributionNodes[i].descendants().slice(1))
    .enter()
    .append("circle")
    .attr("r", d => d.r)
    .attr("fill", d => d.parent.data.color)
    .attr("cx", d => d.x)
    .attr("cy", d => d.y);

  // This method listens for each "tick" event of the d3 forces simulation, which modifies
  // the theme nodes data coordinates (x, y, r) progressively, so we report them to the
  // displayed elements in the graph.
  simulation
    .nodes(themeNodes)
    .on("tick", () => {
      // Move the inner contributions cluster position in space according to the new
      // theme node position
      themeInnerContributionsCluster.attr("transform", (themeNode, i) => {
        const r = d3.select(themeCircles._groups[0][i]).attr("r");
        const x = themeNode.x - r * 0.9;
        const y = themeNode.y - r * 0.9;

        return `translate(${x}, ${y})`;
      });

      // Move the inner contributions circle position in space according to the new
      // theme node position
      themeNodeGroups
        .selectAll("circle")
        .attr("cx", themeNode => themeNode.x)
        .attr("cy", themeNode => themeNode.y);

      // Move the inner theme label position in space according to the new  theme node
      // position
      themeNodeGroups
        .selectAll(".label")
        // Animating the foreignObject label through its transform works better in Safari
        // than from its x and y coordinates
        .attr("transform", themeNode => {
          const x = themeNode.x - getSideLength(themeNode) / 2;
          const y = themeNode.y - getSideLength(themeNode) / 2;

          return `translate(${x}, ${y})`;
        });
    })
    .restart();

  const dispatchThemeEvent = (themeId, eventName) => {
    const linkedGroup = themeNodeGroups._groups[0].find(
      row => row.__data__.id == themeId
    );
    d3.select(linkedGroup).dispatch(eventName);

    const linkedNode = themeCircles._groups[0].find(row => row.__data__.id == themeId);
    d3.select(linkedNode).dispatch(eventName);
  };

  // Add mouse "over" and "out" listeners to the sidebar sub-themes icons, to make
  // hovering those icons display the corresponding theme circle tooltip.
  d3.selectAll("[data-sub-theme-icon]")
    .on("mouseover", function (d, i, j) {
      const themeId = d3.select(j[i]).attr("data-sub-theme-icon");
      dispatchThemeEvent(themeId, "mouseover");
    })
    .on("mouseout", function (d, i, j) {
      const themeId = d3.select(j[i]).attr("data-sub-theme-icon");
      dispatchThemeEvent(themeId, "mouseout");
    });
}

const ContributionsDatavizGraphAnimation_participativeModule = graphql__7778e03bdf58833d3f70;

export default ContributionsDatavizGraphAnimation;

const intlMessages = defineMessages({
  contributions: {
    id: "participativeModule.contributionsDataviz.graphAnimation.contributions",
    defaultMessage:
      "{count, plural, one { {count} contribution } other { {count} contributions }}"
  }
});
